import React, { useEffect, useMemo, useState } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import HeaderOne from "../components/header/HeaderOne";
import FooterOne from "../components/footer/FooterOne";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/+$/, "");

/*
|--------------------------------------------------------------------------
| HTML ENTITY ÇÖZ
|--------------------------------------------------------------------------
|
| Örnek:
| &uuml;    -> ü
| &Ccedil;  -> Ç
| &ouml;    -> ö
|
|--------------------------------------------------------------------------
*/
const decodeHtml = (value = "") => {
  if (!value) {
    return "";
  }

  const textarea = document.createElement("textarea");

  textarea.innerHTML = String(value);

  return textarea.value;
};

/*
|--------------------------------------------------------------------------
| TÜRKÇE BÜYÜK HARF
|--------------------------------------------------------------------------
*/
const toTrUpper = (value = "") => {
  return String(value).toLocaleUpperCase("tr-TR");
};

/*
|--------------------------------------------------------------------------
| REGEX GÜVENLİ HALE GETİR
|--------------------------------------------------------------------------
*/
const escapeRegExp = (value = "") => {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/*
|--------------------------------------------------------------------------
| ARANAN KELİMEYİ VURGULA
|--------------------------------------------------------------------------
*/
const HighlightText = ({ text, query }) => {
  const cleanText = decodeHtml(text);

  const cleanQuery = String(query || "").trim();

  if (!cleanText || !cleanQuery) {
    return cleanText;
  }

  const regex = new RegExp(`(${escapeRegExp(cleanQuery)})`, "gi");

  const parts = cleanText.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch =
          part.toLocaleLowerCase("tr-TR") ===
          cleanQuery.toLocaleLowerCase("tr-TR");

        if (isMatch) {
          return (
            <mark key={index} className="demirsan-search-highlight">
              {part}
            </mark>
          );
        }

        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
};

function SearchResults() {
  const location = useLocation();

  const navigate = useNavigate();

  /*
  |--------------------------------------------------------------------------
  | URL PARAMETRELERİ
  |--------------------------------------------------------------------------
  */

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const query = params.get("q") || "";

  const lang = params.get("lang") || "tr";

  /*
  |--------------------------------------------------------------------------
  | STATE
  |--------------------------------------------------------------------------
  */

  const [searchText, setSearchText] = useState(query);

  const [results, setResults] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  /*
  |--------------------------------------------------------------------------
  | URL DEĞİŞİRSE INPUT'U GÜNCELLE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setSearchText(query);
  }, [query]);

  /*
  |--------------------------------------------------------------------------
  | API ARAMASI
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const cleanQuery = query.trim();

    if (cleanQuery.length < 2) {
      setResults([]);
      return;
    }

    const controller = new AbortController();

    const fetchResults = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${API_BASE_URL}/api/search?q=${encodeURIComponent(
            cleanQuery,
          )}&lang=${encodeURIComponent(lang)}`,
          {
            method: "GET",

            headers: {
              Accept: "application/json",
            },

            signal: controller.signal,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.message || "Arama yapılamadı.");
        }

        setResults(Array.isArray(data?.results) ? data.results : []);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("SEARCH_RESULTS_ERROR:", error);

          setError(error.message || "Arama sırasında bir hata oluştu.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      controller.abort();
    };
  }, [query, lang]);

  useEffect(() => {
    const cleanQuery = searchText.trim();

    /*
     * Input tamamen boşaldıysa arama URL'sini temizle.
     */
    if (cleanQuery.length === 0) {
      if (query) {
        navigate(`/search?lang=${encodeURIComponent(lang)}`, {
          replace: true,
        });
      }

      return;
    }

    /*
     * 1 karakterde henüz arama yapma.
     */
    if (cleanQuery.length < 2) {
      return;
    }

    /*
     * Zaten mevcut sorgu buysa tekrar arama yapma.
     */
    if (cleanQuery === query.trim()) {
      return;
    }

    const timeoutId = setTimeout(() => {
      navigate(
        `/search?q=${encodeURIComponent(
          cleanQuery,
        )}&lang=${encodeURIComponent(lang)}`,
        {
          replace: true,
        },
      );
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText, query, lang, navigate]);
  /*
  |--------------------------------------------------------------------------
  | SAYFA İÇİ TEKRAR ARAMA
  |--------------------------------------------------------------------------
  */

  const handleSearch = (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    const cleanQuery = searchText.trim();

    if (cleanQuery.length < 2) {
      return;
    }

    navigate(
      `/search?q=${encodeURIComponent(cleanQuery)}&lang=${encodeURIComponent(
        lang,
      )}`,
    );
  };

  return (
    <>
      <HeaderOne />

      <main className="demirsan-search-page">
        {/* ====================================================
            ÜST ALAN
        ===================================================== */}
        <section className="demirsan-search-hero">
          <div className="container">
            <div className="demirsan-search-hero-inner">
              <span className="demirsan-search-eyebrow">SİTE İÇİ ARAMA</span>

              <h1>Arama Sonuçları</h1>

              {query && (
                <p className="demirsan-search-query-text">
                  <strong>“{query}”</strong> için sonuçlar
                </p>
              )}

              {/* ==============================================
                  SAYFA İÇİ ARAMA
              =============================================== */}
              <div className="demirsan-search-box">
                <input
                  type="text"
                  value={searchText}
                  placeholder="Anahtar kelime ile ara"
                  autoComplete="off"
                  onChange={(event) => {
                    setSearchText(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch(event);
                    }
                  }}
                />

                <button
                  type="button"
                  className="demirsan-search-submit"
                  onClick={handleSearch}
                  aria-label="Ara"
                  title="Ara"
                >
                  <i className="far fa-search" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ====================================================
            SONUÇLAR
        ===================================================== */}
        <section className="demirsan-search-content">
          <div className="container">
            {/* LOADING */}
            {loading && (
              <div className="demirsan-search-status">
                <div className="demirsan-search-loader" />

                <p>Arama yapılıyor...</p>
              </div>
            )}

            {/* ERROR */}
            {!loading && error && (
              <div className="demirsan-search-status demirsan-search-error">
                <i className="far fa-exclamation-circle" />

                <p>{error}</p>
              </div>
            )}

            {!loading && !error && query.trim().length < 2 && (
              <div className="demirsan-search-empty">
                <div className="demirsan-search-empty-icon">
                  <i className="far fa-search" />
                </div>

                <h3>Aramaya başlayın</h3>

                <p>Sonuçları görmek için en az 2 harf yazın.</p>
              </div>
            )}
            {/* SONUÇLAR */}
            {!loading && !error && results.length > 0 && (
              <>
                <div className="demirsan-search-count">
                  <strong>{results.length}</strong> sonuç bulundu
                </div>

                <div className="demirsan-search-results">
                  {results.map((result, index) => (
                    <Link
                      key={`${result.url}-${index}`}
                      to={result.url}
                      className="demirsan-search-result"
                    >
                      <div className="demirsan-search-result-content">
                        {/* KATEGORİLER */}
                        {/* HİYERARŞİ */}
                        {Array.isArray(result.hierarchy) &&
                          result.hierarchy.length > 0 && (
                            <div className="demirsan-search-result-hierarchy">
                              {result.hierarchy.map((item, hierarchyIndex) => (
                                <React.Fragment
                                  key={`${item}-${hierarchyIndex}`}
                                >
                                  {hierarchyIndex > 0 && (
                                    <span className="demirsan-search-result-hierarchy-separator">
                                      ›
                                    </span>
                                  )}

                                  <span className="demirsan-search-result-hierarchy-item">
                                    {toTrUpper(item)}
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          )}

                        {/* BAŞLIK - hiyerarşide zaten varsa tekrar gösterme */}
                        {!(
                          Array.isArray(result.hierarchy) &&
                          result.hierarchy.length > 0 &&
                          String(
                            result.hierarchy[result.hierarchy.length - 1] || "",
                          )
                            .trim()
                            .toLocaleLowerCase("tr-TR") ===
                            String(result.title || "")
                              .trim()
                              .toLocaleLowerCase("tr-TR")
                        ) && (
                          <h2>
                            <HighlightText text={result.title} query={query} />
                          </h2>
                        )}

                        {/* SADECE EŞLEŞEN KISA AÇIKLAMA */}
                        {result.snippet && (
                          <p>
                            <HighlightText
                              text={result.snippet}
                              query={query}
                            />
                          </p>
                        )}
                      </div>

                      <div className="demirsan-search-result-arrow">
                        <i className="far fa-arrow-right" />
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      </main>

      <FooterOne />

      <style>
        {`

        /*
        |--------------------------------------------------------------------------
        | SAYFA
        |--------------------------------------------------------------------------
        */

        .demirsan-search-page {
          min-height: 650px;

          background:
            #f7f8fa;
        }


        /*
        |--------------------------------------------------------------------------
        | ÜST ALAN
        |--------------------------------------------------------------------------
        */

        .demirsan-search-hero {
          padding:
            58px 0 45px;

          background:
            #ffffff;

          border-bottom:
            1px solid #eceef2;
        }


        .demirsan-search-hero-inner {
          width: 100%;
          max-width: 760px;

          margin: 0 auto;

          text-align:
            center;
        }


        .demirsan-search-eyebrow {
          display: block;

          margin-bottom:
            7px;

          color:
            #e3262e;

          font-size:
            12px;

          font-weight:
            700;

          letter-spacing:
            1px;
        }


        .demirsan-search-hero h1 {
          margin: 0;

          color:
            #0b1735;

          font-size:
            37px;

          font-weight:
            700;

          line-height:
            1.2;
        }


        .demirsan-search-query-text {
          margin:
            11px 0 0;

          color:
            #727d90;

          font-size:
            14px;
        }


        .demirsan-search-query-text strong {
          color:
            #3a465b;
        }


        /*
        |--------------------------------------------------------------------------
        | ARAMA KUTUSU
        |--------------------------------------------------------------------------
        */

        .demirsan-search-box {
          display: flex !important;

          flex-direction:
            row !important;

          align-items:
            center !important;

          width: 100% !important;

          height: 58px;

          margin:
            26px auto 0;

          padding:
            5px 5px 5px 0;

          overflow: hidden;

          border:
            1px solid #dfe3e9;

          border-radius:
            10px;

          background:
            #ffffff;

          box-shadow:
            0 6px 20px
            rgba(
              11,
              23,
              53,
              .055
            );
        }


        /*
         * Tema CSS'i input veya button'a
         * width:100% basarsa ez.
         */
        .demirsan-search-box input {
          position:
            static !important;

          display:
            block !important;

          flex:
            1 1 auto !important;

          width:
            auto !important;

          min-width:
            0 !important;

          height:
            48px !important;

          margin:
            0 !important;

          padding:
            0 18px !important;

          border:
            0 !important;

          outline:
            0 !important;

          background:
            transparent !important;

          box-shadow:
            none !important;

          color:
            #17233c !important;

          font-family:
            inherit !important;

          font-size:
            14px !important;

          line-height:
            48px !important;
        }


        .demirsan-search-box input::placeholder {
          color:
            #a2a9b5;
        }


        /*
        |--------------------------------------------------------------------------
        | KÜÇÜK ARAMA BUTONU
        |--------------------------------------------------------------------------
        */

        .demirsan-search-box
        .demirsan-search-submit {
          position:
            static !important;

          display:
            inline-flex !important;

          align-items:
            center !important;

          justify-content:
            center !important;

          flex:
            0 0 48px !important;

          width:
            48px !important;

          min-width:
            48px !important;

          max-width:
            48px !important;

          height:
            48px !important;

          min-height:
            48px !important;

          max-height:
            48px !important;

          margin:
            0 !important;

          padding:
            0 !important;

          border:
            0 !important;

          border-radius:
            8px !important;

          background:
            #e3262e !important;

          color:
            #ffffff !important;

          box-shadow:
            none !important;

          cursor:
            pointer;

          transition:
            background-color
              .2s ease,
            transform
              .2s ease;
        }


        .demirsan-search-box
        .demirsan-search-submit:hover {
          background:
            #bd171e !important;

          transform:
            translateY(-1px);
        }


        .demirsan-search-box
        .demirsan-search-submit
        i {
          display:
            block !important;

          margin:
            0 !important;

          padding:
            0 !important;

          color:
            inherit !important;

          font-size:
            17px !important;

          line-height:
            1 !important;
        }


        /*
        |--------------------------------------------------------------------------
        | RESULTS
        |--------------------------------------------------------------------------
        */

        .demirsan-search-content {
          padding:
            40px 0 80px;
        }


        .demirsan-search-count {
          margin-bottom:
            14px;

          color:
            #818a9a;

          font-size:
            13px;
        }


        .demirsan-search-count strong {
          color:
            #0b1735;
        }


        .demirsan-search-results {
          display: flex;

          flex-direction:
            column;

          gap: 11px;
        }


        /*
        |--------------------------------------------------------------------------
        | RESULT CARD
        |--------------------------------------------------------------------------
        */

        .demirsan-search-result {
          position:
            relative;

          display:
            flex !important;

          align-items:
            center;

          justify-content:
            space-between;

          gap: 22px;

          width: 100%;

          padding:
            20px 23px;

          border:
            1px solid #e3e7ed;

          border-radius:
            10px;

          background:
            #ffffff;

          color:
            inherit !important;

          text-decoration:
            none !important;

          box-shadow:
            0 4px 14px
            rgba(
              0,
              0,
              0,
              .03
            );

          transition:
            border-color
              .2s ease,
            box-shadow
              .2s ease,
            transform
              .2s ease;
        }


        .demirsan-search-result:hover {
          border-color:
            rgba(
              227,
              38,
              46,
              .36
            );

          transform:
            translateY(-1px);

          box-shadow:
            0 8px 22px
            rgba(
              0,
              0,
              0,
              .06
            );
        }


        .demirsan-search-result-content {
          flex: 1;

          min-width: 0;
        }


        /*
        |--------------------------------------------------------------------------
        | TITLE
        |--------------------------------------------------------------------------
        */

        .demirsan-search-result h2 {
          margin: 0;

          color:
            #0b1735;

          font-size:
            18px;

          font-weight:
            650;

          line-height:
            1.4;
        }


        /*
        |--------------------------------------------------------------------------
        | SNIPPET
        |--------------------------------------------------------------------------
        */

        .demirsan-search-result p {
          display:
            -webkit-box;

          margin:
            7px 0 0;

          overflow:
            hidden;

          color:
            #697487;

          font-size:
            13px;

          line-height:
            1.6;

          -webkit-line-clamp:
            2;

          -webkit-box-orient:
            vertical;
        }


        /*
        |--------------------------------------------------------------------------
        | ARANAN KELİME VURGUSU
        |--------------------------------------------------------------------------
        */

        .demirsan-search-highlight {
          display:
            inline;

          margin: 0;

          padding:
            1px 3px;

          border-radius:
            3px;

          background:
            #ffe7e8;

          color:
            #d71920;

          font: inherit;

          font-weight:
            700;
        }


        /*
        |--------------------------------------------------------------------------
        | OK
        |--------------------------------------------------------------------------
        */

        .demirsan-search-result-arrow {
          display: flex;

          align-items:
            center;

          justify-content:
            center;

          flex:
            0 0 36px;

          width:
            36px;

          height:
            36px;

          border-radius:
            50%;

          background:
            #fff0f1;

          color:
            #e3262e;

          font-size:
            13px;

          transition:
            background-color
              .2s ease,
            color
              .2s ease,
            transform
              .2s ease;
        }


        .demirsan-search-result:hover
        .demirsan-search-result-arrow {
          background:
            #e3262e;

          color:
            #ffffff;

          transform:
            translateX(2px);
        }


        /*
        |--------------------------------------------------------------------------
        | STATUS
        |--------------------------------------------------------------------------
        */

        .demirsan-search-status,
        .demirsan-search-empty {
          padding:
            60px 20px;

          border:
            1px solid #e3e7ed;

          border-radius:
            10px;

          background:
            #ffffff;

          text-align:
            center;
        }


        .demirsan-search-status p {
          margin:
            12px 0 0;

          color:
            #687386;
        }


        .demirsan-search-error {
          color:
            #d51e26;
        }


        .demirsan-search-loader {
          width:
            28px;

          height:
            28px;

          margin:
            0 auto;

          border:
            3px solid #eceff3;

          border-top-color:
            #e3262e;

          border-radius:
            50%;

          animation:
            demirsanSearchSpin
            .7s linear infinite;
        }


        @keyframes demirsanSearchSpin {
          to {
            transform:
              rotate(360deg);
          }
        }


        /*
        |--------------------------------------------------------------------------
        | EMPTY
        |--------------------------------------------------------------------------
        */

        .demirsan-search-empty-icon {
          display: flex;

          align-items:
            center;

          justify-content:
            center;

          width:
            54px;

          height:
            54px;

          margin:
            0 auto 14px;

          border-radius:
            50%;

          background:
            #fff0f1;

          color:
            #e3262e;

          font-size:
            19px;
        }


        .demirsan-search-empty h3 {
          margin:
            0 0 6px;

          color:
            #0b1735;

          font-size:
            19px;
        }


        .demirsan-search-empty p {
          margin: 0;

          color:
            #758094;

          font-size:
            13px;
        }


        /*
        |--------------------------------------------------------------------------
        | MOBILE
        |--------------------------------------------------------------------------
        */

        @media (
          max-width: 767px
        ) {

          .demirsan-search-hero {
            padding:
              42px 0 32px;
          }


          .demirsan-search-hero h1 {
            font-size:
              28px;
          }


          .demirsan-search-box {
            height:
              54px;

            margin-top:
              21px;
          }


          .demirsan-search-box input {
            height:
              44px !important;

            padding:
              0 13px !important;

            font-size:
              13px !important;
          }


          .demirsan-search-box
          .demirsan-search-submit {
            flex-basis:
              44px !important;

            width:
              44px !important;

            min-width:
              44px !important;

            max-width:
              44px !important;

            height:
              44px !important;

            min-height:
              44px !important;

            max-height:
              44px !important;
          }


          .demirsan-search-content {
            padding:
              28px 0 55px;
          }


          .demirsan-search-result {
            gap: 14px;

            padding:
              17px;
          }


          .demirsan-search-result h2 {
            font-size:
              16px;
          }


          .demirsan-search-result p {
            font-size:
              12px;

            -webkit-line-clamp:
              2;
          }


          .demirsan-search-result-arrow {
            flex-basis:
              32px;

            width:
              32px;

            height:
              32px;
          }

        }
          /*
|--------------------------------------------------------------------------
| SEARCH RESULT KATEGORİLER
|--------------------------------------------------------------------------
*/

.demirsan-search-result-categories {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 8px;

  margin-bottom: 10px;
}


.demirsan-search-result-category {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  width: auto;

  padding: 4px 10px 4px 8px;

  border: 1px solid #e7e9ee;
  border-radius: 999px;

  background: #f6f7f9;

  color: #5b6577;

  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: .4px;
  line-height: 1.5;
  text-transform: uppercase;

  transition:
    background-color .2s ease,
    border-color .2s ease,
    color .2s ease;
}


.demirsan-search-result-category::before {
  content: "";

  width: 5px;
  height: 5px;

  border-radius: 50%;

  background: #c7cdd8;

  transition: background-color .2s ease;
}


.demirsan-search-result:hover
.demirsan-search-result-category {
  border-color: rgba(227, 38, 46, .25);

  background: #fff0f1;

  color: #d92129;
}


.demirsan-search-result:hover
.demirsan-search-result-category::before {
  background: #e3262e;
}
/*
|--------------------------------------------------------------------------
| SEARCH RESULT HİYERARŞİ
|--------------------------------------------------------------------------
*/

.demirsan-search-result-hierarchy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 7px;

  margin-bottom: 10px;
}


/* OVAL ETİKET */
.demirsan-search-result-hierarchy-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  width: auto;

  padding: 4px 10px 4px 8px;

  border: 1px solid #e7e9ee;
  border-radius: 999px;

  background: #f6f7f9;
  color: #5b6577;

  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  line-height: 1.5;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}


/* ESKİSİ GİBİ KÜÇÜK NOKTA */
.demirsan-search-result-hierarchy-item::before {
  content: "";

  flex: 0 0 5px;

  width: 5px;
  height: 5px;

  border-radius: 50%;

  background: #c7cdd8;

  transition: background-color 0.2s ease;
}


.demirsan-search-result-hierarchy-separator {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 10px;

  color: #929aaa;

  font-size: 18px;
  font-weight: 800;
  line-height: 1;

  transform: translateY(-1px);
}

/* CARD HOVER */
.demirsan-search-result:hover
.demirsan-search-result-hierarchy-item {
  border-color: rgba(227, 38, 46, 0.25);

  background: #fff0f1;
  color: #d92129;
}

.demirsan-search-result:hover
.demirsan-search-result-hierarchy-item::before {
  background: #e3262e;
}

.demirsan-search-result:hover
.demirsan-search-result-hierarchy-separator {
  color: #d92129;
}
        `}
      </style>
    </>
  );
}

export default SearchResults;
